import { useState, useEffect, useCallback } from 'react';
import { Ticket, Plus, Trash2, AlertCircle, Loader } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { buildTeamApiService } from '../../services/buildTeamApi';
import type { BuildTicketLink } from '../../types/buildTeam';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { TicketIdLink } from '../TicketIdLink';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface BuildTicketsSectionProps {
  targetId: string;
  targetType: 'project' | 'build';
  canEdit: boolean;
  onUpdate: () => void;
}

function linkedAtMs(linkedAt: number): number {
  return linkedAt > 1e12 ? linkedAt : linkedAt * 1000;
}

export function BuildTicketsSection({
  targetId,
  targetType,
  canEdit,
  onUpdate,
}: BuildTicketsSectionProps) {
  const [tickets, setTickets] = useState<BuildTicketLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTicketId, setNewTicketId] = useState('');
  const [isAddingTicket, setIsAddingTicket] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<number | null>(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        targetType === 'project'
          ? await buildTeamApiService.getProjectTickets(targetId)
          : await buildTeamApiService.getBuildTickets(targetId);
      setTickets(data);
      const uuids = new Set<string>();
      data.forEach((t) => {
        if (t.linked_by) uuids.add(t.linked_by);
      });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const validateTicketId = (ticketId: string): boolean => {
    if (!ticketId || ticketId.trim() === '') return false;
    const num = parseInt(ticketId, 10);
    if (Number.isNaN(num) || num <= 0) return false;
    if (ticketId.startsWith('0') && ticketId.length > 1) return false;
    return true;
  };

  const handleLinkTicket = async () => {
    if (!canEdit) return;
    if (!validateTicketId(newTicketId)) {
      alert('Invalid ticket ID. Must be a positive integer and cannot have a leading zero.');
      return;
    }
    const ticketId = parseInt(newTicketId, 10);
    setIsAddingTicket(true);
    try {
      if (targetType === 'project') {
        await buildTeamApiService.linkTicketToProject(targetId, ticketId);
      } else {
        await buildTeamApiService.linkTicketToBuild(targetId, ticketId);
      }
      setNewTicketId('');
      await fetchTickets();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link ticket.');
    } finally {
      setIsAddingTicket(false);
    }
  };

  const handleUnlink = async (ticketId: number) => {
    if (!canEdit || unlinkingId !== null) return;
    setConfirmUnlinkId(ticketId);
  };

  const executeUnlink = async () => {
    if (confirmUnlinkId === null) return;
    const ticketId = confirmUnlinkId;
    setUnlinkingId(ticketId);
    setConfirmUnlinkId(null);
    try {
      if (targetType === 'project') {
        await buildTeamApiService.unlinkTicketFromProject(targetId, ticketId);
      } else {
        await buildTeamApiService.unlinkTicketFromBuild(targetId, ticketId);
      }
      setTickets((prev) => prev.filter((t) => t.ticket_id !== ticketId));
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink ticket.');
    } finally {
      setUnlinkingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Ticket className="w-4 h-4 text-blue-400" />
          Linked Tickets
          <span className="badge badge-neutral">{tickets.length}</span>
        </h3>
        {canEdit && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              step={1}
              value={newTicketId}
              onChange={(e) => setNewTicketId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLinkTicket()}
              placeholder="Ticket ID"
              className="glass-input w-32 tabular-nums"
              aria-label="Ticket ID"
            />
            <button
              type="button"
              onClick={handleLinkTicket}
              disabled={isAddingTicket || !newTicketId}
              className="glass-button shrink-0"
            >
              {isAddingTicket ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Link
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="subcard p-3 space-y-2">
              <div className="flex items-center gap-2">
                <SkeletonLoader className="w-9 h-9 rounded-lg" />
                <div className="space-y-1.5 flex-1">
                  <SkeletonLoader className="h-4 w-24" />
                  <SkeletonLoader className="h-3 w-20" />
                </div>
              </div>
              <SkeletonLoader className="h-3 w-32" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && tickets.length === 0 && (
        <EmptyState
          bare
          compact
          icon={Ticket}
          title="No tickets linked"
          description={canEdit ? 'Enter a ticket ID above to link one.' : 'Linked support tickets will appear here.'}
        />
      )}

      {!loading && !error && tickets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {tickets.map((ticket) => (
            <div key={ticket.ticket_id} className="subcard p-3 space-y-2">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="icon-tile icon-tile-info">
                    <Ticket className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">
                      <TicketIdLink ticketId={ticket.ticket_id} className="text-white hover:text-accent-300 hover:underline underline-offset-2">
                        Ticket #{ticket.ticket_id}
                      </TicketIdLink>
                    </p>
                    <p className="text-xs text-glass-400 tabular-nums">
                      {formatDistanceToNow(new Date(linkedAtMs(ticket.linked_at)), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleUnlink(ticket.ticket_id)}
                    disabled={unlinkingId === ticket.ticket_id}
                    className={cn(
                      'btn-icon text-red-400 hover:text-red-300 touch-manipulation',
                      unlinkingId === ticket.ticket_id && 'cursor-wait'
                    )}
                    title="Unlink ticket"
                    aria-label="Unlink ticket"
                  >
                    {unlinkingId === ticket.ticket_id ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              <p className="text-xs text-glass-400">
                Linked by: <CreatorProfile creatorId={ticket.linked_by} inline />
              </p>
            </div>
          ))}
        </div>
      )}

      {confirmUnlinkId !== null && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmUnlinkId(null)}
          onConfirm={executeUnlink}
          title="Unlink Ticket"
          message={`Are you sure you want to unlink ticket #${confirmUnlinkId}?`}
          confirmText="Unlink"
          isLoading={!!unlinkingId}
        />
      )}
    </div>
  );
}
